import Alert from '@/components/Alert/Alert'
import buildScreenHeaderItems from '@/components/buildScreenHeaderItems'
import { Badge, BadgeText } from '@/components/ui/badge'

import { Divider } from '@/components/ui/divider'
import { HStack } from '@/components/ui/hstack'
import { Image } from '@/components/ui/image'
import { Pressable } from '@/components/ui/pressable'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import { cn } from '@/lib/cn'
import useWebBackButton from '@/lib/hooks/useWebBackButton'
import { handleClickToPay } from '@/modules/handleClickToPay'
import {
  getSubAccountsThunk,
  loadMoreSubAccountsThunk,
  selectAllSubAccounts,
  type SubAccount,
} from '@/redux/slices/subAccountSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { androidHeaderSearchBarThemeProps } from '@/theme'
import { PlanCode, UserRole, UserStatus } from '@/typings/domain'
import { Theme, useTheme } from '@react-navigation/native'
import { FlashList } from '@shopify/flash-list'
import { Stack, useRouter } from 'expo-router'
import { debounce } from 'lodash-es'
import { ChevronRightIcon, CircleUserIcon } from 'lucide-react-native'
import { useCallback, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.Owner]: '拥有者',
  [UserRole.Admin]: '管理员',
  [UserRole.Worker]: '协作者',
  [UserRole.Outworker]: '查看员',
}

export default function Users() {
  const insets = useSafeAreaInsets()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const router = useRouter()
  const headerLeftItems = useWebBackButton('/profile')
  const currentUser = useAppSelector(state => state.currentUser.user)
  const subAccounts = useAppSelector(selectAllSubAccounts)
  const hasNextPage = useAppSelector(state => state.subAccounts.hasNextPage)
  const isLoadingMore = useAppSelector(state => state.subAccounts.isLoadingMore)
  const usageAndBalance = useAppSelector(state => state.usageAndBalance)
  const isOwnerOrAdmin = useAppSelector(state => state.currentUser.isOwnerOrAdmin)

  const [searchText, setSearchText] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const trimmedSearchText = useMemo(() => searchText.trim(), [searchText])
  const hasAddSubAccountAbility = currentUser?.billingAccountAbilities?.hasAddSubAccountAbility
  const availableQuota = usageAndBalance.usersQuota - usageAndBalance.occupiedUsersCount

  const debouncedSearch = useRef(
    debounce((keyword: string) => {
      setIsSearching(true)
      dispatch(
        getSubAccountsThunk({
          keyword: keyword || undefined,
          includeInviteLinks: false,
        })
      ).finally(() => {
        setIsSearching(false)
      })
    }, 300)
  ).current

  const handleSearchTextChange = useCallback(
    (text: string) => {
      const trimmedNewSearchText = text.trim()

      setSearchText(text)

      if (trimmedNewSearchText === '' && trimmedSearchText === '') {
        return
      }

      debouncedSearch(trimmedNewSearchText)
    },
    [trimmedSearchText, debouncedSearch]
  )

  // 下拉刷新
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true)
    dispatch(
      getSubAccountsThunk({
        keyword: trimmedSearchText || undefined,
        includeInviteLinks: true,
      })
    ).finally(() => {
      setIsRefreshing(false)
    })
  }, [dispatch, trimmedSearchText])

  // 加载更多
  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || isLoadingMore) {
      return
    }
    dispatch(
      loadMoreSubAccountsThunk({
        keyword: trimmedSearchText || undefined,
      })
    )
  }, [dispatch, hasNextPage, isLoadingMore, trimmedSearchText])

  // 导航到用户详情
  const handleUserPress = useCallback(
    (subAccount: SubAccount) => {
      router.push(`/profile/users/${subAccount.id}`)
    },
    [router]
  )

  const renderItem = useCallback(
    ({ item, index }: { item: SubAccount; index: number }) => {
      return <UserItem item={item} isLast={index === subAccounts.length - 1} theme={theme} onPress={handleUserPress} />
    },
    [handleUserPress, subAccounts.length, theme]
  )

  const handleAddUser = useCallback(() => {
    if (hasAddSubAccountAbility) {
      if (availableQuota <= 0) {
        // TODO: 购买团队成员名额
        Alert.alert(
          '套餐余量不足',
          `当前已使用 ${usageAndBalance.occupiedUsersCount} 个企业成员，超出配额上限 ${Math.abs(availableQuota)} 个。请禁用超出的企业成员，或者购买更多`,
          [
            { text: '我知道了', style: 'cancel' },
            // {
            //   text: '点此购买',
            //   style: 'default',
            //   onPress: () => {
            //     router.push('/profile/planQuota')
            //   },
            // },
          ]
        )
        return
      }

      router.push('/profile/invite_user')
    } else {
      handleClickToPay({ recommendedPlanCode: PlanCode.E10, currentUser: currentUser ?? undefined })
    }
  }, [hasAddSubAccountAbility, availableQuota, usageAndBalance.occupiedUsersCount, currentUser, router])

  const ListHeader = useCallback(
    () => <Text className="text-secondary-label px-6 pb-4">邀请成员加入团队，分配不同权限协同工作。</Text>,
    []
  )

  const renderListFooter = useCallback(() => {
    if (!isLoadingMore) {
      return null
    }
    return (
      <View className="items-center py-4">
        <ActivityIndicator size="small" />
        <Text size="caption-1" className="mt-2 text-secondary-label">
          加载中...
        </Text>
      </View>
    )
  }, [isLoadingMore])

  return (
    <>
      <Stack.Screen
        options={{
          headerSearchBarOptions: {
            placeholder: '搜索姓名、邮箱或手机号',
            hideWhenScrolling: false,
            ...androidHeaderSearchBarThemeProps(theme),
            onChangeText: (event: any) => {
              handleSearchTextChange(event.nativeEvent.text)
            },
          },
          ...buildScreenHeaderItems({
            left: headerLeftItems,
            right: isOwnerOrAdmin
              ? [
                  {
                    type: 'button',
                    label: '邀请成员',
                    icon: { name: 'person.badge.plus', type: 'sfSymbol' },
                    onPress: handleAddUser,
                  },
                ]
              : undefined,
          }),
        }}
      />
      {isSearching ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" />
          <Text className="mt-4 text-gray-500">搜索中...</Text>
        </View>
      ) : subAccounts.length === 0 && trimmedSearchText ? (
        <View className="flex-1 items-center justify-center">
          <Text className="text-lg text-gray-500">未找到匹配的团队成员</Text>
          <Text className="mt-2 text-sm text-gray-400">请尝试其他关键词</Text>
        </View>
      ) : (
        <FlashList
          contentInsetAdjustmentBehavior="automatic"
          contentContainerStyle={{ paddingTop: 16, paddingBottom: insets.bottom + 16 }}
          data={subAccounts}
          renderItem={renderItem}
          keyExtractor={item => item.id}
          ListHeaderComponent={isSearching || subAccounts.length === 0 ? undefined : ListHeader}
          ListFooterComponent={renderListFooter}
          refreshing={isRefreshing}
          onRefresh={handleRefresh}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.1}
        />
      )}
    </>
  )
}

function UserItem({
  item,
  isLast,
  theme,
  onPress,
}: {
  item: SubAccount
  isLast: boolean
  theme: Theme
  onPress?: (item: SubAccount) => void
}) {
  const displayName = item.name || item.email || item.mobile || '未命名'
  const formCount = item.createdFormsCount || 0
  const entryCount = item.entriesCountOfCreatedForms || 0
  const roleLabel = item.role ? ROLE_LABELS[item.role] : ''

  return (
    <Pressable className="data-[active=true]:bg-background-100" onPress={() => onPress?.(item)}>
      <HStack className="px-6 py-4 items-center" space="md">
        {item.avatarUrl ? (
          <Image source={{ uri: item.avatarUrl }} size="none" className="w-8 h-8 rounded-full" alt={displayName} />
        ) : (
          <CircleUserIcon size={28} color={theme.colors.primary} />
        )}
        <VStack space="sm" className="flex-1">
          <HStack space="md">
            <Text
              size="body"
              className={cn(item.status === UserStatus.Disabled ? 'text-secondary-label' : 'text-label')}
            >
              {displayName}
            </Text>
            {item.status === UserStatus.Disabled && (
              <Badge size="sm" variant="outline" action="error">
                <BadgeText>已禁用</BadgeText>
              </Badge>
            )}
          </HStack>
          <Text size="subhead" className="text-secondary-label" isTruncated>
            表单: {formCount} | 数据: {entryCount.toLocaleString()} | {roleLabel}
          </Text>
        </VStack>
        {onPress && <ChevronRightIcon size={20} color={theme.colors.border} />}
      </HStack>
      {!isLast && <Divider />}
    </Pressable>
  )
}
